import React, { useState } from "react";
import { Mic, Loader2, Send, Church, GraduationCap, Users, Video, Heart, CheckCircle2, CalendarCheck, ArrowRight } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { Honeypot } from "@/components/Honeypot";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { toast } from "sonner";

const FORMATS = [
  { icon: Mic, title: "Keynote Address", text: "An inspiring, story-driven keynote on resilience, hope, and suicide prevention." },
  { icon: GraduationCap, title: "School Assemblies", text: "Age-appropriate talks that reach students with courage and compassion." },
  { icon: Church, title: "Faith & Community", text: "Messages of hope and humanitarian purpose for churches and gatherings." },
  { icon: Users, title: "Workshops & Panels", text: "Interactive sessions on mental wellness, storytelling, and rebuilding." },
  { icon: Video, title: "Virtual Events", text: "Live online talks and fireside chats for audiences anywhere." },
  { icon: Heart, title: "Fundraisers & Galas", text: "Heartfelt talks that move audiences to action for the mission." },
];

const TOPICS = [
  "Choosing to Stay: A Story of Survival & Hope",
  "Rebuilding After the Darkest Night",
  "Raising Super Kids with Resilient Hearts",
  "The Gift of Showing Up: Living as a Humanitarian",
  "Turning Pain into Purpose",
];

export default function Speaking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", event_type: "", event_date: "", location: "", audience_size: "", budget: "", message: "", hp: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type || !form.message) {
      toast.error("Please fill in your name, email, event type, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/speaking", form);
      setDone(true);
      toast.success("Your request has been received — thank you! We'll be in touch soon. 💛");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div data-testid="speaking-page">
      <PageHero
        eyebrow="Invite Mary to Speak"
        title="Invite a message of hope to your stage"
        subtitle="Book our founder to share stories of resilience and suicide prevention — in keynotes, schools, churches, workshops, and virtual events that leave audiences uplifted and equipped."
        image={IMG.caringHands}
        testid="speaking-hero"
      />

      {/* Meet Your Speaker */}
      <section className="bg-bsn-midnight text-bsn-parchment overflow-hidden" data-testid="speaker-section">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24 grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-2">
            <div className="relative mx-auto w-full max-w-sm">
              <div className="absolute -inset-4 rounded-[2.25rem] bg-bsn-gold/25 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-bsn-gold/40 shadow-2xl bg-black ring-1 ring-bsn-gold/20">
                <img
                  src="/speaker/founder.webp"
                  alt="Founder and keynote speaker, Bright Stories Network"
                  className="w-full h-auto object-cover"
                  data-testid="speaker-photo"
                />
                <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 -60px 70px -30px rgba(11,25,44,0.9)" }} aria-hidden="true" />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-bsn-gold text-bsn-midnight px-5 py-2 rounded-full font-semibold text-sm shadow-lg flex items-center gap-2 whitespace-nowrap" data-testid="speaker-badge">
                <Mic className="w-4 h-4" /> Keynote Speaker
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-3">
            <div>
              <p className="eyebrow text-bsn-gold">Meet Your Speaker</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-bsn-champagne mt-2 leading-tight">
                A voice of hope, forged in lived experience
              </h2>
              <p className="text-bsn-ice/90 leading-relaxed mt-5 text-lg">
                Bright Stories Network was founded by a survivor whose own journey through the darkest nights
                became a calling to help others stay. She speaks from lived experience — with honesty, warmth,
                and hard-won hope.
              </p>
              <p className="text-bsn-ice/75 leading-relaxed mt-4">
                Whether on a keynote stage, in a school gym, or a quiet sanctuary, her message is the same:
                your story isn't over, and none of us heals alone. Audiences leave feeling seen, uplifted,
                and equipped to show up for themselves and one another.
              </p>
              <div className="flex flex-wrap gap-2.5 mt-7" data-testid="speaker-credentials">
                {["Suicide-Prevention Advocate", "Survivor & Humanitarian", "Founder, Bright Stories Network"].map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 text-sm font-semibold text-bsn-champagne bg-bsn-navy/70 border border-bsn-gold/25 rounded-full px-4 py-1.5">
                    <CheckCircle2 className="w-4 h-4 text-bsn-gold" /> {c}
                  </span>
                ))}
              </div>
              <a href="#request-form" className="mt-8 inline-flex items-center gap-2 btn-gold font-semibold px-7 py-3 rounded-full transition-all" data-testid="speaker-cta">
                Invite Her to Speak <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Formats */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
        <SectionTitle eyebrow="Ways to Engage" title="Talks shaped for your audience" subtitle="Every talk is tailored — tell us your event and we'll craft the right message together." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FORMATS.map((f, i) => (
            <Reveal key={f.title} delay={i * 70}>
              <div className="h-full bg-white rounded-2xl p-7 card-tactile" data-testid={`speaking-format-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-bsn-gold/15 flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-bsn-bronze" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-bsn-midnight">{f.title}</h3>
                <p className="text-sm text-bsn-sapphire/80 mt-2 leading-relaxed">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Signature topics */}
      <section className="bg-bsn-canvas border-y border-bsn-gold/20">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <SectionTitle eyebrow="Signature Talks" title="Popular topics" />
          <div className="space-y-3">
            {TOPICS.map((t, i) => (
              <Reveal key={t} delay={i * 60}>
                <div className="flex items-center gap-3 bg-white rounded-xl p-4 card-tactile">
                  <CheckCircle2 className="w-5 h-5 text-bsn-gold shrink-0" />
                  <span className="font-serif text-lg text-bsn-midnight">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Request form */}
      <section id="request-form" className="bg-bsn-midnight text-bsn-parchment scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20">
          <div className="text-center mb-10">
            <CalendarCheck className="w-10 h-10 text-bsn-gold mx-auto mb-4" />
            <SectionTitle eyebrow="Book a Talk" title="Request a speaking engagement" subtitle="Share a few details and we'll respond to plan dates, format, and travel together." light />
          </div>

          {done ? (
            <Reveal>
              <div className="bg-bsn-navy/60 border border-bsn-gold/30 rounded-2xl p-10 text-center" data-testid="speaking-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-5" />
                <h3 className="font-serif text-2xl font-semibold text-bsn-champagne">Thank you — your request is in!</h3>
                <p className="text-bsn-ice/80 mt-3">We've received your details and will reach out personally to plan the engagement. Keep an eye on your inbox.</p>
                <Button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", organization: "", event_type: "", event_date: "", location: "", audience_size: "", budget: "", message: "", hp: "" }); }} className="mt-6 btn-gold font-semibold" data-testid="speaking-another">
                  Submit another request
                </Button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="bg-bsn-navy/60 rounded-2xl p-6 sm:p-8 border border-bsn-gold/25 space-y-4" data-testid="speaking-form">
                <Honeypot value={form.hp} onChange={(v) => set("hp", v)} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input placeholder="Your name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-name" />
                  <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-phone" />
                  <Input placeholder="Organization / Church / School" value={form.organization} onChange={(e) => set("organization", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-org" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                    <SelectTrigger className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment" data-testid="speaking-type"><SelectValue placeholder="Event type *" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Keynote">Keynote Address</SelectItem>
                      <SelectItem value="School Assembly">School Assembly</SelectItem>
                      <SelectItem value="Faith / Church">Faith / Church Event</SelectItem>
                      <SelectItem value="Workshop / Panel">Workshop / Panel</SelectItem>
                      <SelectItem value="Virtual Event">Virtual Event</SelectItem>
                      <SelectItem value="Fundraiser / Gala">Fundraiser / Gala</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-date" />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Input placeholder="Location / City" value={form.location} onChange={(e) => set("location", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-location" />
                  <Input placeholder="Audience size" value={form.audience_size} onChange={(e) => set("audience_size", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-audience" />
                  <Input placeholder="Budget (optional)" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-budget" />
                </div>
                <Textarea rows={5} placeholder="Tell us about your event and what you're hoping for... *" value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="speaking-message" />
                <Button type="submit" disabled={loading} className="w-full btn-gold font-semibold h-12" data-testid="speaking-submit">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Speaking Request</>}
                </Button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
